// Bridge between the persisted entities and the projection engine.
//
// The simulation store needs a local preview so the scenario switch answers in
// well under one second without waiting for the `simuler` Edge Function. The
// preview must use the exact same inputs as the server, otherwise the two would
// disagree. Every derivation of those inputs lives here, isolated and testable.
import { HYPOTHESES_DEFAUT, SCENARIOS } from '@horizon/coeur-calcul';
import type {
  CodeScenario,
  FluxDate,
  FluxExceptionnel,
  ParamsSimulation,
  PointSerie,
} from '@horizon/coeur-calcul';
import type { Echeance, Simulation, SimulerOutput } from '@horizon/types';

/** One point of the annual chart series, in euros. */
export interface PointAnnuel {
  /** Year index, 1 based, as displayed on the X axis (A1..AN). */
  readonly annee: number;
  readonly capital: number;
  readonly cumul: number;
}

/** The four indicators of a scenario, in the naming used by the interface. */
export interface KpisScenario {
  readonly capitalEstime: number;
  readonly totalVerse: number;
  readonly rendementAnnualise: number | null;
  readonly plusValueLatente: number;
  readonly plusValuePct: number | null;
  /** Reference variation chips, published for the demonstration screen only. */
  readonly variationCapitalPct: number | null;
  readonly variationRendementPt: number | null;
}

/** Whole months between two dates, floored, never negative. */
export function moisEntre(debut: Date, fin: Date): number {
  const mois = (fin.getFullYear() - debut.getFullYear()) * 12 + (fin.getMonth() - debut.getMonth());
  const ajuste = fin.getDate() < debut.getDate() ? mois - 1 : mois;
  return Math.max(0, ajuste);
}

/** Month zero of a simulation: its start date, else the date it was created. */
export function debutSimulation(simulation: Simulation): Date {
  const debut = simulation.date_debut ?? null;
  return debut !== null ? new Date(`${debut}T00:00:00`) : new Date(simulation.cree_le);
}

/** Parse an ISO date column into a local Date anchored at midnight. */
function dateEcheance(valeur: string): Date {
  return new Date(`${valeur}T00:00:00`);
}

/**
 * Validated contributions feeding the KPI and the IRR (RG-06, RG-07).
 * Arbitrages carry no cash and are excluded, as are non validated rows.
 */
export function versementsValides(
  echeances: readonly Echeance[],
  debut: Date,
): readonly FluxDate[] {
  const flux: FluxDate[] = [];
  for (const echeance of echeances) {
    if (echeance.statut !== 'valide' || echeance.type === 'arbitrage') {
      continue;
    }
    if (echeance.montant === null) {
      continue;
    }
    flux.push({
      mois: moisEntre(debut, dateEcheance(echeance.date_echeance)),
      montant: echeance.montant,
    });
  }
  return flux;
}

/**
 * Exceptional dated flows fed to the projection. The monthly contribution is
 * already carried by `vm`, so only the exceptional rows are injected here.
 */
export function fluxExceptionnels(
  echeances: readonly Echeance[],
  debut: Date,
): readonly FluxExceptionnel[] {
  const flux: FluxExceptionnel[] = [];
  for (const echeance of echeances) {
    if (echeance.type !== 'versement_exceptionnel' || echeance.montant === null) {
      continue;
    }
    if (echeance.statut === 'annule' || echeance.statut === 'rejete') {
      continue;
    }
    const mois = moisEntre(debut, dateEcheance(echeance.date_echeance));
    if (mois >= 1) {
      flux.push({ mois, montant: echeance.montant });
    }
  }
  return flux;
}

/**
 * Normalise the organisation hypotheses into the three scenario codes, falling
 * back to the versioned defaults when a row is missing for a scenario.
 */
export function rendementsNormalises(
  bruts: Readonly<Record<string, number>>,
): Record<CodeScenario, number> {
  const sortie = {} as Record<CodeScenario, number>;
  for (const scenario of SCENARIOS) {
    const valeur = bruts[scenario];
    sortie[scenario] =
      typeof valeur === 'number' && Number.isFinite(valeur)
        ? valeur
        : HYPOTHESES_DEFAUT.scenarios[scenario];
  }
  return sortie;
}

/** Build the engine inputs of a simulation from its persisted rows. */
export function construireParams(
  simulation: Simulation,
  echeances: readonly Echeance[],
  rendements: Record<CodeScenario, number>,
  maintenant: Date = new Date(),
): ParamsSimulation {
  const debut = debutSimulation(simulation);
  const flux = fluxExceptionnels(echeances, debut);
  const moisMax = simulation.horizon_annees * 12;
  const base = {
    v0: simulation.versement_initial,
    vm: simulation.versement_mensuel,
    horizonAnnees: simulation.horizon_annees,
    fraisAnnuels: simulation.frais_gestion,
    rendementsBruts: rendements,
    moisCourant: Math.min(moisEntre(debut, maintenant), moisMax),
    versementsValides: versementsValides(echeances, debut),
  };
  return flux.length > 0 ? { ...base, flux } : base;
}

/** Convert an annual sampling of the engine into the chart shape. */
export function versPointsAnnuels(points: readonly PointSerie[]): readonly PointAnnuel[] {
  return points.map((point, index) => ({
    annee: index + 1,
    capital: point.capital,
    cumul: point.cumul,
  }));
}

/** Amounts in thousands of euros, the unit expected by HzChartProjection. */
export function versKiloEuros(points: readonly PointAnnuel[]): readonly number[] {
  return points.map((point) => Math.round((point.capital / 1000) * 100) / 100);
}

/** Cumulated contributions in thousands of euros, the dashed series of the chart. */
export function cumulKiloEuros(points: readonly PointAnnuel[]): readonly number[] {
  return points.map((point) => Math.round((point.cumul / 1000) * 100) / 100);
}

/** Adapt the snake_case payload of the `simuler` function to the interface shape. */
export function normaliserSortieServeur(sortie: SimulerOutput): {
  readonly series: Record<CodeScenario, readonly PointAnnuel[]>;
  readonly kpis: Record<CodeScenario, KpisScenario>;
  readonly hypothesesVersion: number;
} {
  const series = {} as Record<CodeScenario, readonly PointAnnuel[]>;
  const kpis = {} as Record<CodeScenario, KpisScenario>;
  for (const scenario of SCENARIOS) {
    const points = sortie.series[scenario] ?? [];
    series[scenario] = points.map((point) => ({
      annee: point.annee,
      capital: point.capital,
      cumul: point.cumul,
    }));
    const brut = sortie.kpis[scenario];
    kpis[scenario] =
      brut === undefined
        ? {
            capitalEstime: 0,
            totalVerse: 0,
            rendementAnnualise: null,
            plusValueLatente: 0,
            plusValuePct: null,
            variationCapitalPct: null,
            variationRendementPt: null,
          }
        : {
            capitalEstime: brut.capital_estime,
            totalVerse: brut.total_verse,
            rendementAnnualise: brut.tri_annualise,
            plusValueLatente: brut.plus_value,
            plusValuePct: brut.plus_value_pct,
            variationCapitalPct: brut.variation_capital_pct ?? null,
            variationRendementPt: brut.variation_rendement_pt ?? null,
          };
  }
  return { series, kpis, hypothesesVersion: sortie.hypotheses_version };
}
